import { memo, useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Form from "../../components/Form.js";
import { getAnimals, getAnimalsRegistrationData, getAllAnimalsRegistrationData } from "../../api/index.js";
import { jwt } from "../../utils/index.js";
import { Box, Typography, Button, Checkbox, List, ListItem, ListItemButton, ListItemText, ListItemIcon, TextField, Pagination } from "@mui/material";
import Spinner from "../../components/Spinner.js";
import useSelectStyles from "../../components/styles/Select";
import SelectOwnerForm from "../../components/forms/OwnerSelect.js";
import { useSnackbar } from "../../utils/index.js";
import AnimalRegistrationPdf, { AnimalRegistrationDocument } from "../../components/info-pdfs/AnimalsRegistration.js";
import { pdf } from "@react-pdf/renderer";
import { BackButton } from "../../components/Buttons.js";

const ITEMS_PER_PAGE = 50;

const AnimalRegistration = () => {
    const [selectedOwner, setSelectedOwner] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isKentroUser, setIsKentroUser] = useState(false);

    useEffect(() => {
        jwt.isKentroUser().then((status) => {
            setIsKentroUser(status);
        });
    }, []);
    const [fetchedAnimals, setFetchedAnimals] = useState([]);
    const [fetchedData, setFetchedData] = useState([]);
    const [step, setStep] = useState(1);
    const [selectedForAction, setSelectedForAction] = useState([]);
    const [viewingAnimalIndex, setViewingAnimalIndex] = useState(null);
    const [loadingProgress, setLoadingProgress] = useState({ current: 0, total: 0 });
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);

    const classes = useSelectStyles();
    const { error } = useSnackbar();
    const navigate = useNavigate();

    // Filter animals based on search term
    const filteredData = useMemo(() => {
        if (!searchTerm.trim()) {
            return fetchedData;
        }
        return fetchedData.filter(animalEntry => {
            const regCode = animalEntry.animal?.registrationCode || '';
            return regCode.toLowerCase().includes(searchTerm.toLowerCase().trim());
        });
    }, [fetchedData, searchTerm]);

    // Calculate pagination
    const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
    const paginatedData = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        return filteredData.slice(startIndex, endIndex);
    }, [filteredData, currentPage]);

    // Handle page change
    const handlePageChange = (_, value) => {
        setCurrentPage(value);
        setSelectedForAction([]);
    };

    // Handle search change
    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
        setCurrentPage(1);
        setSelectedForAction([]);
    };

    // Helper function to sort animals by registrationCode ascending
    const sortByRegistrationCode = (data) => {
        return [...data].sort((a, b) => {
            const codeA = a.animal?.registrationCode || '';
            const codeB = b.animal?.registrationCode || '';
            return codeA.localeCompare(codeB);
        });
    };

    const fetchAnimals = async (selOwner) => {
        setIsLoading(true);
        try {
            const { success: scs, animals, message } = await getAnimals(selOwner);
            if (scs) {
                setFetchedAnimals(animals);
                setStep(2);
            } else {
                error(message);
            }
        } catch (err) {
            error(err);
        }
        setIsLoading(false);
    };

    const selectAnimalFormContent = [
        {
            customType: "input",
            id: "animalCodes",
            placeholder: "Εισάγετε Κωδικούς Ζώων",
            multiline: true,
            rows: 4,
        },
        {
            customType: "checkbox",
            id: "allAnimals",
            label: "Εκτύπωση Πιστοποιητικών για όλα τα ζώα της εκτροφής",
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Υποβολή"
        },
    ];

    // Helper function to parse animal codes input
    const parseAnimalCodes = (input, ownerPrefix) => {
        const codes = [];
        const parts = input.split(',').map(part => part.trim()).filter(part => part);

        for (const part of parts) {
            // Check if it's a range (e.g., "0356-0423")
            if (part.includes('-')) {
                const [start, end] = part.split('-').map(p => p.trim());

                // If both are 4-digit codes, expand the range
                if (start.length === 4 && end.length === 4) {
                    const startNum = parseInt(start, 10);
                    const endNum = parseInt(end, 10);

                    for (let i = startNum; i <= endNum; i++) {
                        const fourDigit = i.toString().padStart(4, '0');
                        codes.push(`${ownerPrefix}${fourDigit}`);
                    }
                }
            } else {
                // Single code
                if (part.length === 4) {
                    // 4-digit code: add owner prefix
                    codes.push(`${ownerPrefix}${part}`);
                } else if (part.length === 12) {
                    // 12-digit code: use as is
                    codes.push(part);
                }
            }
        }

        return codes;
    };

    const handleAnimalSelect = async (values) => {
        setIsLoading(true);

        try {
            if (values?.allAnimals) {
                // Fetch all animals' registration data for the owner
                const { success: scs, data, message } = await getAllAnimalsRegistrationData(selectedOwner?._id);
                if (scs) {
                    // The backend returns an array where each item contains all registration data
                    // We need to restructure it to match the expected format
                    const allData = data.map((registrationData) => ({
                        animal: registrationData.animal,
                        data: registrationData,
                    }));

                    // Sort by registrationCode ascending
                    const sortedData = sortByRegistrationCode(allData);

                    setFetchedData(sortedData);
                    setIsLoading(false);
                    setStep(3);
                    return;
                } else {
                    error(message);
                    setIsLoading(false);
                    return;
                }
            }

            // Get owner's 8-digit prefix (breeding code)
            const ownerPrefix = selectedOwner?.code || '';

            // Parse the input to get all animal codes
            const parsedCodes = parseAnimalCodes(values?.animalCodes, ownerPrefix);

            if (parsedCodes.length === 0) {
                error('Δεν βρέθηκαν έγκυροι κωδικοί ζώων.');
                setIsLoading(false);
                return;
            }

            // Find all matching animals from fetched animals
            const matchingAnimals = fetchedAnimals.filter((animal) =>
                parsedCodes.includes(animal?.registrationCode)
            );

            if (matchingAnimals.length === 0) {
                error('Δεν βρέθηκαν ζώα με τους κωδικούς που δώσατε.');
                setIsLoading(false);
                return;
            }

            // Fetch registration data for all selected animals
            const allData = [];
            setLoadingProgress({ current: 0, total: matchingAnimals.length });

            for (let i = 0; i < matchingAnimals.length; i++) {
                const animal = matchingAnimals[i];
                setLoadingProgress({ current: i + 1, total: matchingAnimals.length });

                const { success: scs, data, message } = await getAnimalsRegistrationData(animal?._id);
                if (scs) {
                    allData.push({ animal, data });
                } else {
                    error(`Σφάλμα για το ζώο ${animal?.registrationCode}: ${message}`);
                }
            }

            setLoadingProgress({ current: 0, total: 0 });

            if (allData.length > 0) {
                // Sort by registrationCode ascending
                const sortedData = sortByRegistrationCode(allData);

                setFetchedData(sortedData);
                setStep(3);
            }
        } catch (err) {
            error(err);
        }
        setIsLoading(false);
    };

    // Toggle selection for an animal
    const handleToggleSelection = (globalIndex) => {
        setSelectedForAction((prev) => {
            if (prev.includes(globalIndex)) {
                return prev.filter((i) => i !== globalIndex);
            } else {
                return [...prev, globalIndex];
            }
        });
    };

    // Select all animals
    const handleSelectAll = () => {
        if (searchTerm.trim()) {
            // With search: select ALL matching animals (across all pages)
            const allFilteredIndices = filteredData.map(animalEntry => {
                return fetchedData.findIndex(fd => fd.animal?._id === animalEntry.animal?._id);
            }).filter(idx => idx !== -1);

            if (selectedForAction.length === allFilteredIndices.length) {
                // Deselect all
                setSelectedForAction([]);
            } else {
                // Select all filtered
                setSelectedForAction(allFilteredIndices);
            }
        } else {
            // Without search: select only current page
            const currentPageIndices = paginatedData.map(animalEntry => {
                return fetchedData.findIndex(fd => fd.animal?._id === animalEntry.animal?._id);
            }).filter(idx => idx !== -1);

            const allCurrentPageSelected = currentPageIndices.every(idx => selectedForAction.includes(idx));

            if (allCurrentPageSelected) {
                // Deselect current page
                setSelectedForAction(prev => prev.filter(idx => !currentPageIndices.includes(idx)));
            } else {
                // Select current page
                setSelectedForAction(prev => [...new Set([...prev, ...currentPageIndices])]);
            }
        }
    };

    // View a single animal's PDF
    const handleViewPdf = (index) => {
        setViewingAnimalIndex(index);
        setStep(4);
    };

    // Download selected PDFs
    const handleDownloadSelected = async () => {
        setIsLoading(true);
        try {
            const selectedData = selectedForAction.map(index => fetchedData[index]);
            const doc = <AnimalRegistrationDocument
                selectedOwner={selectedOwner}
                fetchedData={selectedData}
            />;
            const blob = await pdf(doc).toBlob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Γενεαλογικα_Πιστοποιητικα_${selectedData.length}_Ζωων.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(() => {
                URL.revokeObjectURL(url);
            }, 1000);
        } catch (err) {
            error('Σφάλμα κατά τη λήψη των πιστοποιητικών.');
            console.error(err);
        }
        setIsLoading(false);
    };

    // Download all PDFs (from currently selected or filtered)
    const handleDownloadAll = async () => {
        setIsLoading(true);
        try {
            const dataToDownload = searchTerm.trim() ? filteredData : fetchedData;
            const doc = <AnimalRegistrationDocument
                selectedOwner={selectedOwner}
                fetchedData={dataToDownload}
            />;
            const blob = await pdf(doc).toBlob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Γενεαλογικα_Πιστοποιητικα_${dataToDownload.length}_Ζωων.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(() => {
                URL.revokeObjectURL(url);
            }, 1000);
        } catch (err) {
            error('Σφάλμα κατά τη λήψη των πιστοποιητικών.');
            console.error(err);
        }
        setIsLoading(false);
    };

    // Print selected PDFs
    const handlePrintSelected = async () => {
        setIsLoading(true);
        try {
            const selectedData = selectedForAction.map(index => fetchedData[index]);
            const doc = <AnimalRegistrationDocument
                selectedOwner={selectedOwner}
                fetchedData={selectedData}
            />;
            const blob = await pdf(doc).toBlob();
            const url = URL.createObjectURL(blob);

            // Open in new window for printing
            const printWindow = window.open(url);
            if (printWindow) {
                printWindow.onload = () => {
                    printWindow.print();
                };
            }
        } catch (err) {
            error('Σφάλμα κατά την εκτύπωση των πιστοποιητικών.');
            console.error(err);
        }
        setIsLoading(false);
    };

    // Print all PDFs (from currently selected or filtered)
    const handlePrintAll = async () => {
        setIsLoading(true);
        try {
            const dataToPrint = searchTerm.trim() ? filteredData : fetchedData;
            const doc = <AnimalRegistrationDocument
                selectedOwner={selectedOwner}
                fetchedData={dataToPrint}

            />;
            const blob = await pdf(doc).toBlob();
            const url = URL.createObjectURL(blob);

            // Open in new window for printing
            const printWindow = window.open(url);
            if (printWindow) {
                printWindow.onload = () => {
                    printWindow.print();
                };
            }
        } catch (err) {
            error('Σφάλμα κατά την εκτύπωση των πιστοποιητικών.');
            console.error(err);
        }
        setIsLoading(false);
    };

    return (
        <>
            <Spinner
                open={isLoading}
                progressText={
                    loadingProgress.total > 0
                        ? `Φόρτωση δεδομένων: ${loadingProgress.current} / ${loadingProgress.total} ζώα`
                        : undefined
                }
            />
            {step === 1 && (
                <>
                    <SelectOwnerForm
                        setSelectedOwner={setSelectedOwner}
                        title={"Γενεαλογικό Πιστοποιητικό Ζώου"}
                        method={fetchAnimals}
                        screenCase={isKentroUser ? "AnimalRegistrationKentro" : "AnimalRegistration"}
                    />
                    {!isKentroUser && (
                        <Box sx={{ display: "flex", justifyContent: "center", marginTop: "1rem" }}>
                            <Button
                                variant="filled"
                                onClick={() => navigate("/tools/registration-logs")}
                                sx={{ textTransform: "none", backgroundColor: "secondary.main", color: "white !important" }}
                            >
                                Προβολή Ιστορικού Αναζητήσεων
                            </Button>
                        </Box>
                    )}
                </>
            )}
            {step === 2 && (
                <>
                    <Box className={classes.outerBox}>
                        <Box container flexDirection="column" className={classes.innerBox}>
                            <Typography variant="h5" sx={{ margin: "1rem" }}>
                                Εισάγετε τους Κωδικούς των Ζώων για τα οποία θέλετε να εκδόσετε Γενεαλογικό Πιστοποιητικό
                            </Typography>
                            <Box sx={{ margin: "1rem", padding: "1rem", backgroundColor: "#f5f5f5", borderRadius: "4px" }}>
                                <Typography variant="body2" sx={{ marginBottom: "0.5rem" }}>
                                    • Μπορείτε να εισάγετε είτε Κωδικούς Εκτροφής (4-ψήφιους), είτε Κωδικούς Γενεαλογικού Βιβλίου (12-ψήφιους).
                                </Typography>
                                <Typography variant="body2" sx={{ marginBottom: "0.5rem" }}>
                                    • Μπορείτε να εισάγετε κωδικούς ή/και εύρος κωδικών χωρίζοντάς τα με κόμμα. (Π.χ. 0010, 999999990023, 0356-0423).
                                </Typography>
                                <Typography variant="body2" sx={{ marginBottom: "0.5rem" }}>
                                    • Στην περίπτωση εύρους Κωδικών Εκτροφής (Πχ. 0010 - 0015) προστίθεται αυτόματα το 8-ψήφιο πρόθεμα του Κωδικού Εκτροφής του ιδιοκτήτη.
                                </Typography>
                                <Typography variant="body2">
                                    • Στην περίπτωση πολλαπλών κωδικών εκτροφής (π.χ. 999999990023, 989898980023) η χρήση του 4-ψήφιου κωδικού, επιστρέφει το πιστοποιητικό του ζώου του οποίου το 8-ψήφιο πρόθεμα ταυτίζεται με τον κωδικό της τρέχουσας εκτροφής. Επομένως, αν θέλετε να εκδόσετε το πιστοποιητικό ενός ζώου που προέρχεται από άλλη εκτροφή, πρέπει να εισάγετε όλοκληρο τον 12-ψήφιο κωδικό του.
                                </Typography>
                            </Box>
                            <Form content={selectAnimalFormContent} onSubmit={handleAnimalSelect} />
                            <BackButton onClick={() => setStep(1)} />
                        </Box>
                    </Box>
                </>
            )}
            {step === 3 && (
                <Box className={classes.outerBox}>
                    <Box container flexDirection="column" className={classes.innerBox}>
                        <Typography variant="h5" sx={{ margin: "1rem", textAlign: "center" }}>
                            Γενεαλογικά Πιστοποιητικά Ζώων
                        </Typography>

                        {/* Search Box */}
                        <Box sx={{ margin: "1rem", display: "flex", justifyContent: "center" }}>
                            <TextField
                                label="Αναζήτηση με Κωδικό Γενεαλογικού Βιβλίου"
                                variant="outlined"
                                value={searchTerm}
                                onChange={handleSearchChange}
                                sx={{ minWidth: "400px" }}
                                placeholder="Εισάγετε κωδικό γενεαλογικού βιβλίου..."
                            />
                        </Box>

                        <Typography variant="body1" sx={{ margin: "1rem", textAlign: "center" }}>
                            {searchTerm.trim()
                                ? `Βρέθηκαν ${filteredData.length} ζώα που ταιριάζουν στην αναζήτηση. Εμφανίζονται ${paginatedData.length} ζώα.`
                                : `Συνολικά ${fetchedData.length} ζώα. Εμφανίζονται ${paginatedData.length} ζώα ανά σελίδα.`
                            }
                        </Typography>

                        {/* Bulk Actions */}
                        <Box sx={{ margin: "1rem", display: "flex", gap: "1rem", flexWrap: "wrap", justifyContent: "center" }}>
                            <Button
                                variant="outlined"
                                onClick={handleSelectAll}
                                sx={{ textTransform: "none", borderColor: "secondary.main", color: "secondary.main" }}
                            >
                                {searchTerm.trim()
                                    ? (selectedForAction.length === filteredData.length ? "Αποεπιλογή Όλων" : `Επιλογή Όλων (${filteredData.length})`)
                                    : (paginatedData.every(ad => selectedForAction.includes(fetchedData.findIndex(fd => fd.animal?._id === ad.animal?._id)))
                                        ? "Αποεπιλογή Σελίδας"
                                        : `Επιλογή Σελίδας (${paginatedData.length})`
                                    )
                                }
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleDownloadSelected}
                                disabled={selectedForAction.length === 0}
                                sx={{ textTransform: "none", backgroundColor: "secondary.main" }}
                            >
                                Λήψη Επιλεγμένων ({selectedForAction.length})
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleDownloadAll}
                                sx={{ textTransform: "none", backgroundColor: "secondary.main" }}
                            >
                                {searchTerm.trim()
                                    ? `Λήψη Όλων των Αποτελεσμάτων (${filteredData.length})`
                                    : `Λήψη Όλων (${fetchedData.length})`
                                }
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handlePrintSelected}
                                disabled={selectedForAction.length === 0}
                                sx={{ textTransform: "none", backgroundColor: "secondary.main" }}
                            >
                                Εκτύπωση Επιλεγμένων ({selectedForAction.length})
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handlePrintAll}
                                sx={{ textTransform: "none", backgroundColor: "secondary.main" }}
                            >
                                {searchTerm.trim()
                                    ? `Εκτύπωση Όλων των Αποτελεσμάτων (${filteredData.length})`
                                    : `Εκτύπωση Όλων (${fetchedData.length})`
                                }
                            </Button>
                        </Box>

                        {/* Pagination Controls (Top) */}
                        {totalPages > 1 && (
                            <Box sx={{ display: "flex", justifyContent: "center", margin: "1rem" }}>
                                <Pagination
                                    count={totalPages}
                                    page={currentPage}
                                    onChange={handlePageChange}
                                    color="secondary"
                                    size="large"
                                    showFirstButton
                                    showLastButton
                                />
                            </Box>
                        )}

                        {/* List of Animals - Paginated */}
                        <List sx={{ width: "100%", bgcolor: "background.paper", marginTop: "1rem" }}>
                            {paginatedData.map((animalEntry) => {
                                const animal = animalEntry.animal;
                                const globalIndex = fetchedData.findIndex(fd => fd.animal?._id === animal._id);
                                return (
                                    <ListItem
                                        key={animal._id || globalIndex}
                                        disablePadding
                                        sx={{
                                            border: "1px solid #e0e0e0",
                                            marginBottom: "0.5rem",
                                            borderRadius: "4px",
                                        }}
                                    >
                                        <ListItemIcon sx={{ minWidth: "auto", paddingLeft: "1rem" }}>
                                            <Checkbox
                                                edge="start"
                                                checked={selectedForAction.includes(globalIndex)}
                                                onChange={() => handleToggleSelection(globalIndex)}
                                            />
                                        </ListItemIcon>
                                        <ListItemButton onClick={() => handleViewPdf(globalIndex)}>
                                            <ListItemText
                                                primary={`Κωδικός: ${animal.registrationCode}`}
                                                secondary={`Ενώτιο: ${animal.flockCode} | Φύλο: ${animal.sex} | Φυλή: ${animal.breedCode}`}
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                );
                            })}
                        </List>

                        {/* Pagination Controls (Bottom) */}
                        {totalPages > 1 && (
                            <Box sx={{ display: "flex", justifyContent: "center", margin: "1rem" }}>
                                <Pagination
                                    count={totalPages}
                                    page={currentPage}
                                    onChange={handlePageChange}
                                    color="secondary"
                                    size="large"
                                    showFirstButton
                                    showLastButton
                                />
                            </Box>
                        )}

                        <BackButton onClick={() => setStep(2)} />
                    </Box>
                </Box>
            )}
            {step === 4 && viewingAnimalIndex !== null && (
                <Box>
                    <BackButton
                        title="Πίσω στη Λίστα"
                        onClick={() => {
                            setViewingAnimalIndex(null);
                            setStep(3);
                        }}
                        sx={{ margin: "1rem" }}
                    />
                    <AnimalRegistrationPdf
                        fetchedData={[fetchedData[viewingAnimalIndex]]}
                        selectedOwner={selectedOwner}
                    />
                </Box>
            )}
        </>
    );
};

export default memo(AnimalRegistration);